import { createContext, useCallback, useContext, useState } from 'react'
import axios from '../api/axios'
import { APIS } from '../api'
import { toast } from 'react-toastify'
import { handleNavigationError } from '../api/error'
import { useNavigate } from 'react-router-dom'
import { componentRoutes } from '../constants/componentRoutes'

export const NewsContext = createContext({
  news: [],
  newsDetail: {},
  newsLoader: false,
  onLoadNews: async () => {},
  onAddNews: async values => {},
  onUpdateNews: async values => {},
  onDeleteNews: async values => {}
})

export const NewsProvider = ({ children }) => {
  const [news, setNews] = useState(null)
  const [newsLoader, setNewsLoader] = useState(false)
  const [newsDetail, setNewsDetail] = useState(null)

  const navigate = useNavigate()

  const onLoadNews = useCallback(async () => {
    setNewsLoader(true)
    try {
      const { data } = await axios.post(APIS.LIST_NEWS)

      if (data.code == 1) {
        setNews(data?.data)
      } else {
        setNews([])
      }
      setNewsLoader(false)
    } catch (error) {
      handleNavigationError(navigate, error)
    } finally {
      setNewsLoader(false)
    }
  }, [navigate])

  const onAddNews = useCallback(
    async values => {
      setNewsLoader(true)
      try {
        const { data } = await axios.post(APIS.ADD_NEWS, values)
        if (data.code == 1) {
          toast.success(data.message)
        } else {
          toast.error(data.message)
        }
        setNewsLoader(false)
        await onLoadNews()
        navigate(componentRoutes.newsList)
      } catch (error) {
        handleNavigationError(navigate, error)
      } finally {
        setNewsLoader(false)
      }
    },
    [onLoadNews, navigate]
  )

  const onUpdateNews = useCallback(
    async values => {
      setNewsLoader(true)
      try {
        setNewsLoader(false)
        const { data } = await axios.post(APIS.UPDATE_NEWS, values)
        
        if (data.code == 1) {
          navigate(componentRoutes.newsList);
          toast.success(data.message)
        } else {
          toast.error(data.message)
        }
        setNewsLoader(false)
        await onLoadNews()
      } catch (error) {
        handleNavigationError(navigate, error)
      } finally {
        setNewsLoader(false)
      }
    },
    [onLoadNews, navigate]
  )

  const onDeleteNews = useCallback(
    async values => {
      setNewsLoader(true)
      const payload = {
        news_id: values
      }
      try {
        const { data } = await axios.post(APIS.DELETE_NEWS, payload)

        if (data.code == 1) {
          toast.success(data.message)
        }
        setNewsLoader(false)
        await onLoadNews()
        return data
      } catch (error) {
        handleNavigationError(navigate, error)
      } finally {
        setNewsLoader(false)
      }
    },
    [onLoadNews, navigate]
  )

  const getNews = useCallback(
    async values => {
      const payload = {
        news_id: values
      }
      setNewsLoader(true)
      try {
        const { data } = await axios.post(APIS.GET_NEWS, payload)
        if (data.code == 1) {
          setNewsDetail(data.data)
        }
      } catch (error) {
        handleNavigationError(navigate, error)
      } finally {
        setNewsLoader(false)
      }
    },
    [navigate]
  )

  const values = {
    news,
    newsDetail,
    newsLoader,
    onLoadNews: useCallback(async () => {
      return await onLoadNews()
    }, [onLoadNews]),
    onAddNews: useCallback(
      async values => {
        return await onAddNews(values)
      },
      [onAddNews]
    ),
    onUpdateNews: useCallback(
      async values => {
        return await onUpdateNews(values)
      },
      [onUpdateNews]
    ),
 
    onDeleteNews: useCallback(
      async values => {
        return await onDeleteNews(values)
      },
      [onDeleteNews]
    ),
    getNews: useCallback(
      async values => {
        return await getNews(values)
      },
      [getNews]
    )
  }

  return <NewsContext.Provider value={values}>{children}</NewsContext.Provider>
}

export const useNews = () => useContext(NewsContext)
